import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import TeamManagement from '../components/TeamManagement';
import CurrentProjects from '../components/CurrentProjects';
import CurrentAttendees from '../components/CurrentAttendees';
import CurrentSponsors from '../components/CurrentSponsors';
import SendMessageModal from '../components/SendMessageModal';
import DecryptedText from '../components/DecryptedText';

const SponsorDashboard: React.FC = () => {
  const { isLoading, user } = useAppContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isSendMessageModalOpen, setIsSendMessageModalOpen] = useState(false);

  // Simplified loading for dashboard
  useEffect(() => {
    if (user) {
      setLoading(false);
    }
  }, [user]);

  if (isLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-purple-800 to-pink-600">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white font-space">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    // Add top padding so content clears the navbar consistently across pages
    <div className="min-h-screen bg-transparent relative overflow-hidden sm:pt-24 pb-12">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-20 left-20 w-32 h-32 bg-white bg-opacity-20 rounded-full blur-xl"></div>
        <div className="absolute top-40 right-32 w-24 h-24 bg-white bg-opacity-15 rounded-full blur-lg"></div>
        <div className="absolute bottom-32 left-16 w-20 h-20 bg-white bg-opacity-25 rounded-full blur-lg"></div>
        <div className="absolute bottom-20 right-20 w-28 h-28 bg-white bg-opacity-20 rounded-full blur-xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <DecryptedText
            text="Host Dashboard"
            animateOn="view"
            revealDirection="start"
            speed={100}
            sequential={true}
            className="text-black text-3xl md:text-3xl font-bold text-left"
          />
          <p className="text-gray-600 mt-1">Manage your events, teams, and sponsors all in one place.</p>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Row 1: Your Events (Full Width) */}
          <div className="md:col-span-3 flex flex-col">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-white/50 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-800 font-coolvetica">Your Events</h2>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsSendMessageModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-white/50 text-black rounded-lg hover:opacity-90 transition-all text-sm font-medium shadow-sm"
                    title="Send Message to Participants"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                    </svg>
                    Send Message
                  </button>
                  <button
                    onClick={() => navigate('/sponsor/events/create')}
                    className="flex items-center gap-2 px-4 py-2 bg-[#93D6D9] border border-black text-black rounded-lg hover:opacity-90 transition-all text-sm font-medium shadow-sm"
                    title="Create New Event"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create New Event
                  </button>
                </div>
              </div>
              <div className="p-6">
                <CurrentProjects compact={false} />
              </div>
            </div>
          </div>

          {/* Row 2: Attendees and Sponsors (side by side) */}
          <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col h-[500px]">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden flex flex-col h-full">
                <div className="px-6 py-4 border-b border-gray-100 bg-white/50 flex justify-between items-center">
                  <h2 className="text-lg font-bold text-gray-800 font-coolvetica">Attendees</h2>
                </div>
                <div className="flex-grow overflow-y-auto p-2 custom-scrollbar">
                  <CurrentAttendees compact={true} />
                </div>
              </div>
            </div>

            <div className="flex flex-col h-[500px]">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden flex flex-col h-full">
                <div className="px-6 py-4 border-b border-gray-100 bg-white/50 flex justify-between items-center">
                  <h2 className="text-lg font-bold text-gray-800 font-coolvetica">Sponsors</h2>
                </div>
                <div className="flex-grow overflow-y-auto p-2 custom-scrollbar">
                  <CurrentSponsors compact={true} />
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Team Management (Full Width) */}
          <div className="md:col-span-3">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden">
              <div className="p-2">
                <TeamManagement sponsorId="me" />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Send Message Modal */}
      {user?.id && (
        <SendMessageModal
          isOpen={isSendMessageModalOpen}
          onClose={() => setIsSendMessageModalOpen(false)}
          sponsorId={user.id}
        />
      )}
    </div>
  );
};

export default SponsorDashboard;
